/**
 * Seat-delivery ETA for active orders (Order Status countdown).
 */

export const MIN_SEAT_ETA_MINUTES = 10;
export const DEFAULT_SEAT_ETA_MINUTES = 15;
export const MAX_SEAT_ETA_MINUTES = 60;

const SQLITE_UTC_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

export function parseOrderTimestamp(value?: string | null): Date | null {
  if (value == null || value === '') return null;

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (trimmed.includes('T')) {
    const parsed = new Date(trimmed);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }

  const sqliteMatch = trimmed.match(SQLITE_UTC_RE);
  if (sqliteMatch) {
    const parsed = new Date(
      `${sqliteMatch[1]}-${sqliteMatch[2]}-${sqliteMatch[3]}T${sqliteMatch[4]}:${sqliteMatch[5]}:${sqliteMatch[6]}Z`
    );
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }

  const fallback = new Date(trimmed);
  return Number.isFinite(fallback.getTime()) ? fallback : null;
}

export function clampEstimatedMinutes(minutes: number | null | undefined): number {
  const n = Number(minutes);
  if (!Number.isFinite(n)) return DEFAULT_SEAT_ETA_MINUTES;
  return Math.min(Math.max(Math.round(n), MIN_SEAT_ETA_MINUTES), MAX_SEAT_ETA_MINUTES);
}

export function buildEstimatedReadyAt(createdAt: string, minutes: number): string | null {
  const base = parseOrderTimestamp(createdAt);
  if (!base) return null;
  const clamped = clampEstimatedMinutes(minutes);
  return new Date(base.getTime() + clamped * 60000).toISOString();
}

export interface SeatEtaDisplay {
  minutes: number;
  almostReady: boolean;
}

export function computeSeatDeliveryEta(input: {
  placedAt?: string;
  estimatedReadyAt?: string;
  estimatedMinutes?: number | null;
}): SeatEtaDisplay | null {
  const placed = parseOrderTimestamp(input.placedAt);
  if (!placed) return null;

  let targetMs: number;
  const parsedReady = input.estimatedReadyAt
    ? parseOrderTimestamp(input.estimatedReadyAt)?.getTime()
    : undefined;

  if (parsedReady != null && Number.isFinite(parsedReady)) {
    targetMs = parsedReady;
  } else {
    const minutes = clampEstimatedMinutes(input.estimatedMinutes);
    targetMs = placed.getTime() + minutes * 60000;
  }

  const remaining = Math.ceil((targetMs - Date.now()) / 60000);

  if (remaining <= 0) {
    return { minutes: 0, almostReady: true };
  }

  const minutes = Math.min(Math.max(remaining, MIN_SEAT_ETA_MINUTES), MAX_SEAT_ETA_MINUTES);
  return { minutes, almostReady: false };
}
